import {
  Bell,
  CheckSquare,
  Clock,
  CornerDownRight,
  Eye,
  FileCheck,
  FileSignature,
  Flag,
  Gavel,
  GitBranch,
  Handshake,
  Hourglass,
  Mail,
  MessageCircle,
  MessageSquareWarning,
  ScrollText,
  Send,
  Shuffle,
  Stamp,
  Tags,
  Timer,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

import type { ProcessoArbitralStatus } from '@/features/secretaria-arbitral/types';

import type { BlocoLocal, GrupoBloco, TipoBloco } from '../types/editor';

import { resumoDoAlvoDaIntimacao } from './destinatarioDaIntimacao';
import { lerEmailsCitacao, resumoEmailsCitacao } from './emailsCitacao';
import { avisaEAguarda, lerAvisoAtraso, rotuloPapeisAviso } from './esperaVencimento';

/**
 * Janela de arrependimento entre a decisão da triagem e o disparo da citação
 * (call de 15/08/2026). Espelha o default do backend em
 * `janelaReversaoCitacao.ts` — bloco sem o campo ganha a janela lá, na execução.
 */
export const JANELA_REVERSAO_PADRAO_MINUTOS = 5;

/** Mínimo aceito pelo produto (a Bianca aceitou baixar de 5 para 3). */
export const JANELA_REVERSAO_MINIMA_MINUTOS = 3;

/**
 * Texto padrão do concluso automático (proposto em 21/10/2026, pendente de confirmação do
 * Brenno). Espelha `TEXTO_PADRAO_CONCLUSO_DO_FLUXO` do executor no core, que o usa quando o bloco
 * chega sem texto.
 */
export const TEXTO_PADRAO_CONCLUSO =
  'Encerrados os prazos de manifestação, faço os autos conclusos ao árbitro para sentença.';

/**
 * 15 DU, alinhado à espera da sentença que vem logo depois no fluxo (aviso de atraso em 15 DU):
 * a régua do concluso manual (5 DU no sumaríssimo) faria o concluso vencer e sumir do painel do
 * árbitro antes do primeiro aviso. Espelha `PRAZO_PADRAO_CONCLUSO_DO_FLUXO_DU` do core.
 */
export const PRAZO_PADRAO_CONCLUSO_DU = 15;

/** Mesmo guard do executor `arbitral.concluso` e do validador de publicação do core (1..60 DU). */
export const FAIXA_PRAZO_CONCLUSO = { min: 1, max: 60 } as const;

export interface BlocoDefinicao {
  tipo: TipoBloco;
  grupo: GrupoBloco;
  nome: string;
  descricao: string;
  icon: LucideIcon;
  labelPadrao: string;
  parametrosPadrao: Record<string, unknown>;
  /**
   * `false` tira o tipo do prompt e do parser da importação por IA. O "Ir para" aponta para o id
   * de um bloco, e a importação gera ids novos: um destino importado nunca existiria.
   */
  importavel?: false;
}

export interface GrupoDefinicao {
  grupo: GrupoBloco;
  nome: string;
  descricao: string;
  icon: LucideIcon;
}

export const GRUPOS: GrupoDefinicao[] = [
  {
    grupo: 'NUCLEO',
    nome: 'Núcleo do Processo',
    descricao: 'Status, andamentos, pendências, documentos, sorteio e encerramento.',
    icon: Workflow,
  },
  {
    grupo: 'COMUNICACAO',
    nome: 'Comunicação',
    descricao: 'E-mail, WhatsApp, notificação no sino e AR físico.',
    icon: Mail,
  },
  {
    grupo: 'CONTROLE_TEMPO',
    nome: 'Controle de Tempo',
    descricao: 'Condições, prazos, esperas e detecção de eventos.',
    icon: Clock,
  },
  {
    grupo: 'FINANCEIRO',
    nome: 'Financeiro',
    descricao: 'Cobrança no Asaas e registro de consumo de convênio.',
    icon: Wallet,
  },
];

export const DEFINICOES: BlocoDefinicao[] = [
  {
    tipo: 'CONDICAO',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Condição (se/senão)',
    descricao:
      'Avalia campos do processo e segue por um de dois caminhos: "se verdadeiro" ou "se falso".',
    icon: GitBranch,
    labelPadrao: 'Condição',
    parametrosPadrao: {
      regras: [],
      conector: 'AND',
      seVerdadeiro: [],
      seFalso: [],
    },
  },
  {
    tipo: 'MUDAR_STATUS',
    grupo: 'NUCLEO',
    nome: 'Mudar status do processo',
    descricao: 'Atualiza o status arbitral do processo para um novo valor.',
    icon: Workflow,
    labelPadrao: 'Mudar status',
    parametrosPadrao: { statusDestino: null },
  },
  {
    tipo: 'SET_SITUACAO',
    grupo: 'NUCLEO',
    nome: 'Definir situação',
    descricao:
      'Marca uma ou mais situações (substatus informativos) no processo. Não muda o status nem o rito.',
    icon: Tags,
    labelPadrao: 'Definir situação',
    parametrosPadrao: { situacaoKeys: [], desativarAnteriores: false },
  },
  {
    tipo: 'CRIAR_ANDAMENTO',
    grupo: 'NUCLEO',
    nome: 'Registrar andamento',
    descricao: 'Cria entrada na timeline do processo (informativo ou sistêmico).',
    icon: ScrollText,
    labelPadrao: 'Registrar andamento',
    parametrosPadrao: { texto: '', tipo: 'INFORMATIVO', modeloId: null },
  },
  {
    tipo: 'CRIAR_PENDENCIA',
    grupo: 'NUCLEO',
    nome: 'Criar pendência',
    descricao: 'Abre pendência ao requerente, requerido, árbitro ou Secretaria.',
    icon: CheckSquare,
    labelPadrao: 'Criar pendência',
    parametrosPadrao: {
      tipo: '',
      titulo: '',
      descricao: '',
      responsavelTipo: 'SISTEMA',
      destinatario: null,
      prazoDiasUteis: 5,
      bloqueiaAvanco: true,
      requerValidacao: false,
      // Sem "Bloquear avanço": a variável em que o id é gravado, lida pela "Pendência vigiada" de
      // um Aguardar evento (core#2249).
      savePendenciaIdToVariable: '',
    },
  },
  {
    tipo: 'GERAR_DOCUMENTO',
    grupo: 'NUCLEO',
    nome: 'Gerar documento',
    descricao: 'Gera um documento/certidão a partir de um modelo cadastrado.',
    icon: FileCheck,
    labelPadrao: 'Gerar documento',
    parametrosPadrao: { categoria: '', publicarAutomatico: false, visivelPara: 'TODOS' },
  },
  {
    tipo: 'PUBLICAR_DECURSO',
    grupo: 'NUCLEO',
    nome: 'Publicar decurso',
    descricao: 'Emite certidão de decurso de prazo. Bloqueia se houver manifestação (G1).',
    icon: Stamp,
    labelPadrao: 'Publicar decurso',
    parametrosPadrao: {},
  },
  {
    tipo: 'PUBLICAR_TRANSITO',
    grupo: 'NUCLEO',
    nome: 'Publicar trânsito em julgado',
    descricao: 'Marca o processo como transitado e encerra o ciclo.',
    icon: Gavel,
    labelPadrao: 'Publicar trânsito',
    parametrosPadrao: { motivoEncerramento: 'SENTENCA_ARBITRAL' },
  },
  {
    tipo: 'FINALIZAR_PROCESSO',
    grupo: 'NUCLEO',
    nome: 'Finalizar processo',
    descricao:
      'Encerra o processo: aplica o status final, registra o motivo e fecha prazos e pendências em aberto.',
    icon: Flag,
    labelPadrao: 'Finalizar processo',
    parametrosPadrao: {
      statusFinal: 'CONCLUIDO',
      motivoEncerramento: 'SENTENCA_ARBITRAL',
      fecharPendencias: true,
    },
  },
  {
    tipo: 'SORTEAR_ARBITRO',
    grupo: 'NUCLEO',
    nome: 'Sortear árbitro',
    descricao: 'Sorteia um árbitro do ranking ponderado do escritório.',
    icon: Shuffle,
    labelPadrao: 'Sortear árbitro',
    parametrosPadrao: { algoritmo: 'pesos_com_carga', excluirArbitroIds: [] },
  },
  {
    tipo: 'EXIGIR_TERMO_IMPARCIALIDADE',
    grupo: 'NUCLEO',
    nome: 'Exigir termo de imparcialidade',
    descricao:
      'Cria a pendência do termo ao(s) árbitro(s) do processo e declara os modelos dos dois termos que o árbitro emite no painel.',
    icon: FileSignature,
    labelPadrao: 'Exigir termo de imparcialidade',
    parametrosPadrao: {
      templateIdTermoImparcialidade: null,
      templateIdTermoArbitragem: null,
    },
  },
  {
    tipo: 'CONCLUSO',
    grupo: 'NUCLEO',
    nome: 'Concluso ao árbitro',
    descricao:
      'Abre o concluso ao árbitro do processo, como a Secretaria faz à mão. Não pausa o fluxo: se já houver concluso em curso, ou faltar árbitro aceito ou termo, segue sem criar e avisa a Secretaria.',
    icon: Gavel,
    labelPadrao: 'Concluso ao árbitro',
    parametrosPadrao: {
      descricao: TEXTO_PADRAO_CONCLUSO,
      prazoDiasUteis: PRAZO_PADRAO_CONCLUSO_DU,
    },
  },
  {
    tipo: 'INTIMAR_SENTENCA',
    grupo: 'COMUNICACAO',
    nome: 'Intimar sentença',
    descricao:
      'Notifica as partes da publicação da sentença (e-mail/WhatsApp pelos contatos cadastrados).',
    icon: Send,
    labelPadrao: 'Intimar sentença',
    parametrosPadrao: {
      intimarRequerente: true,
      templateVersaoId: null,
      modeloEmailId: null,
      assuntoEmail: '',
      lexiFlowId: null,
      gerarDocumentoPdf: true,
    },
  },
  {
    tipo: 'COBRAR_ASAAS',
    grupo: 'FINANCEIRO',
    nome: 'Gerar cobrança no Asaas',
    descricao:
      'Gera a cobrança do requerente no Asaas e aguarda a confirmação do pagamento para liberar as próximas etapas.',
    icon: Wallet,
    labelPadrao: 'Gerar cobrança no Asaas',
    parametrosPadrao: { vencimentoDiasUteis: 7 },
  },
  {
    tipo: 'REGISTRAR_CONSUMO_CONVENIO',
    grupo: 'FINANCEIRO',
    nome: 'Registrar consumo de convênio',
    descricao: 'Registra o consumo do processo no convênio do cliente, sem gerar cobrança.',
    icon: Handshake,
    labelPadrao: 'Registrar consumo de convênio',
    parametrosPadrao: {},
  },
  {
    tipo: 'ENVIAR_EMAIL',
    grupo: 'COMUNICACAO',
    nome: 'Enviar e-mail',
    descricao: 'Envia um e-mail a partir de um modelo cadastrado em Modelos de e-mail.',
    icon: Mail,
    labelPadrao: 'Enviar e-mail',
    parametrosPadrao: { para: 'REQUERIDO', modeloComunicacaoId: null },
  },
  {
    tipo: 'ENVIAR_WHATSAPP',
    grupo: 'COMUNICACAO',
    nome: 'Enviar WhatsApp',
    descricao: 'Envia um WhatsApp a partir de um modelo aprovado em Modelos de WhatsApp.',
    icon: MessageCircle,
    labelPadrao: 'Enviar WhatsApp',
    parametrosPadrao: { para: 'REQUERIDO', modeloComunicacaoId: null },
  },
  {
    tipo: 'NOTIF_INAPP',
    grupo: 'COMUNICACAO',
    nome: 'Notificação no sino',
    descricao: 'Cria um alerta no sino a partir de um modelo cadastrado em Modelos de alerta.',
    icon: Bell,
    labelPadrao: 'Notificar no sino',
    parametrosPadrao: { para: 'SECRETARIA', modeloNotificacaoId: null },
  },
  {
    tipo: 'ENVIAR_AR',
    grupo: 'COMUNICACAO',
    nome: 'Enviar AR físico (Scrybe)',
    descricao:
      'Envia carta física (AR) a partir de um modelo cadastrado em Modelos de carta. Pode ficar condicionado à falta de visualização dos meios digitais (e-mail/WhatsApp).',
    icon: Send,
    labelPadrao: 'Enviar AR físico',
    parametrosPadrao: { modeloCartaId: null, somenteSeEmailNaoAberto: true, prazoDiasUteis: 3 },
  },
  {
    tipo: 'CITACAO',
    grupo: 'COMUNICACAO',
    nome: 'Citação',
    descricao:
      'Aciona o fluxo de citação (Lexi). A Lexi decide o método de envio; opcionalmente, até 3 e-mails são enviados ao requerido em sequência enquanto ele não for citado.',
    icon: Gavel,
    labelPadrao: 'Citação',
    parametrosPadrao: {
      fluxoCitacaoId: null,
      emailsCitacao: [],
      janelaReversaoMinutos: JANELA_REVERSAO_PADRAO_MINUTOS,
    },
  },
  {
    tipo: 'INICIAR_PRAZO',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Iniciar prazo',
    descricao: 'Inicia contagem de prazo em dias úteis com ações no vencimento.',
    icon: Timer,
    labelPadrao: 'Iniciar prazo',
    parametrosPadrao: {
      tipoPrazoId: null,
      nomePrazo: '',
      diasUteis: 15,
      apartirDe: 'HOJE',
      pausar: true,
      aoVencer: [],
      // Modo solto: variável em que o id do prazo fica gravado, para uma espera vigiá-lo (A-9).
      savePrazoIdToVariable: '',
    },
  },
  {
    tipo: 'AGUARDAR_EVENTO',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Aguardar evento',
    descricao: 'Pausa o fluxo até um evento ocorrer ou timeout vencer.',
    icon: Hourglass,
    labelPadrao: 'Aguardar evento',
    parametrosPadrao: {
      eventos: ['arbitral.manifestacao_potencial.v1'],
      timeoutDiasUteis: 7,
      aoOcorrer: [],
      aoTimeout: [],
      // Com "Pendência resolvida" ou "Pendência expirada" entre os eventos: só o desfecho da
      // pendência desta variável retoma (core#2249). Vazio = qualquer pendência do processo.
      pendenciaId: '',
      // Com "Prazo vencido" entre os eventos: só o vencimento do prazo desta variável retoma (A-9).
      prazoVigiado: '',
    },
  },
  {
    tipo: 'AGUARDAR_VISUALIZACAO',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Aguardar visualização',
    descricao:
      'Pausa até todos os envios digitais das notificações anteriores serem visualizados (ou timeout).',
    icon: Eye,
    labelPadrao: 'Aguardar visualização',
    parametrosPadrao: {
      timeoutDiasUteis: 7,
      aoVisualizar: [],
      aoTimeout: [],
    },
  },
  {
    tipo: 'AGUARDAR_CITACAO',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Aguardar citação',
    descricao:
      'Pausa até TODOS os requeridos terem a citação confirmada (ou timeout). Quem define o que vale como citação é o fluxo da Lexi.',
    icon: Eye,
    labelPadrao: 'Aguardar citação',
    parametrosPadrao: {
      timeoutDiasUteis: 7,
      aoCitar: [],
      aoTimeout: [],
    },
  },
  {
    tipo: 'AGUARDAR_EMBARGOS',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Aguardar prazo de embargos',
    descricao: 'Pausa o fluxo aguardando prazo de embargos pós-sentença.',
    icon: Hourglass,
    labelPadrao: 'Aguardar embargos',
    parametrosPadrao: { diasUteis: 5 },
  },
  {
    tipo: 'DETECTAR_MANIFESTACAO',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Detectar manifestação',
    descricao: 'Reage à entrada de manifestação inbound (e-mail/WhatsApp/portal).',
    icon: MessageSquareWarning,
    labelPadrao: 'Detectar manifestação',
    parametrosPadrao: { aoDetectar: [] },
  },
  {
    tipo: 'IR_PARA',
    grupo: 'CONTROLE_TEMPO',
    nome: 'Ir para',
    descricao:
      'Encerra o caminho seguindo para outro bloco do fluxo, de qualquer etapa ou ramo. Não executa nada sozinho: só define o próximo passo.',
    icon: CornerDownRight,
    labelPadrao: 'Ir para',
    parametrosPadrao: { destinoBlocoId: null },
    importavel: false,
  },
];

export const DEFINICAO_POR_TIPO = new Map<TipoBloco, BlocoDefinicao>(
  DEFINICOES.map((def) => [def.tipo, def]),
);

export const BLOCOS_POR_GRUPO = new Map<GrupoBloco, BlocoDefinicao[]>(
  GRUPOS.map((g) => [g.grupo, DEFINICOES.filter((def) => def.grupo === g.grupo)]),
);

export function labelPadraoPorTipo(tipo: TipoBloco): string {
  return DEFINICAO_POR_TIPO.get(tipo)?.labelPadrao ?? tipo;
}

/** Nome do bloco como o canvas o mostra: o rótulo dado pela Secretaria ou, vazio, o do tipo. */
export function tituloDoBloco(bloco: Pick<BlocoLocal, 'tipo' | 'label'>): string {
  return bloco.label?.trim() || labelPadraoPorTipo(bloco.tipo);
}

function resumoVencimentoDaEspera(bloco: {
  tipo: TipoBloco;
  parametros: Record<string, unknown>;
}): string {
  const timeout = (bloco.parametros.timeoutDiasUteis as number) ?? 0;
  if (!avisaEAguarda(bloco)) return `timeout ${timeout} DU`;
  const aviso = lerAvisoAtraso(bloco.parametros.avisoAtraso);
  return `vence em ${timeout} DU → avisa ${rotuloPapeisAviso(aviso.para)} a cada ${aviso.repetirACadaDiasUteis} DU`;
}

export function resumoParametros(bloco: {
  tipo: TipoBloco;
  parametros: Record<string, unknown>;
}): string {
  const { tipo, parametros: p } = bloco;
  switch (tipo) {
    case 'CONDICAO': {
      const regras = Array.isArray(p.regras) ? p.regras : [];
      const conector = (p.conector as string) === 'OR' ? 'OU' : 'E';
      const seV = Array.isArray(p.seVerdadeiro) ? p.seVerdadeiro.length : 0;
      const seF = Array.isArray(p.seFalso) ? p.seFalso.length : 0;
      if (regras.length === 0) return 'Configurar condição';
      const plural = regras.length > 1 ? `${regras.length} regras (${conector})` : '1 regra';
      return `${plural} · ${seV} ação(ões) se sim / ${seF} se não`;
    }
    case 'MUDAR_STATUS': {
      const status = (p.statusDestino as string | null) ?? null;
      return status
        ? `Para "${status.replaceAll('_', ' ').toLowerCase()}"`
        : 'Configurar status destino';
    }
    case 'SET_SITUACAO': {
      const keys = Array.isArray(p.situacaoKeys) ? (p.situacaoKeys as string[]) : [];
      if (keys.length === 0) return 'Selecionar situação(ões)';
      const sufixo = p.desativarAnteriores ? ' · substitui anteriores' : '';
      return `${keys.length} situação(ões)${sufixo}`;
    }
    case 'CRIAR_ANDAMENTO': {
      const texto = (p.texto as string) ?? '';
      return texto ? texto.slice(0, 80) : 'Configurar texto do andamento';
    }
    case 'CONCLUSO': {
      const texto = (p.descricao as string) ?? '';
      const dias = (p.prazoDiasUteis as number) ?? PRAZO_PADRAO_CONCLUSO_DU;
      const inicio = texto.length > 60 ? `${texto.slice(0, 60)}…` : texto;
      return texto ? `${inicio} · ${dias} DU` : 'Configurar texto do concluso';
    }
    case 'CRIAR_PENDENCIA': {
      const titulo = (p.titulo as string) ?? '';
      const dias = (p.prazoDiasUteis as number) ?? 0;
      return titulo ? `${titulo} · ${dias} DU` : 'Configurar pendência';
    }
    case 'GERAR_DOCUMENTO': {
      const categoria = (p.categoria as string) ?? '';
      return categoria
        ? `Categoria: ${categoria.replaceAll('_', ' ').toLowerCase()}`
        : 'Selecionar categoria';
    }
    case 'PUBLICAR_TRANSITO': {
      const motivo = (p.motivoEncerramento as string) ?? '';
      return `Motivo: ${motivo.replaceAll('_', ' ').toLowerCase()}`;
    }
    case 'FINALIZAR_PROCESSO': {
      const motivo = (p.motivoEncerramento as string) ?? '';
      return motivo
        ? `Encerrar · ${motivo.replaceAll('_', ' ').toLowerCase()}`
        : 'Configurar encerramento';
    }
    case 'SORTEAR_ARBITRO': {
      const algo = (p.algoritmo as string) ?? 'pesos_com_carga';
      const excluidos = Array.isArray(p.excluirArbitroIds) ? p.excluirArbitroIds.length : 0;
      const sufixo = excluidos > 0 ? ` · ${excluidos} excluído${excluidos === 1 ? '' : 's'}` : '';
      return `Algoritmo: ${algo.replaceAll('_', ' ')}${sufixo}`;
    }
    case 'ENVIAR_EMAIL':
    case 'ENVIAR_WHATSAPP': {
      const modeloId = (p.modeloComunicacaoId as string | null) ?? null;
      const para = (p.para as string) ?? '';
      return modeloId ? `Para: ${para.toLowerCase()}` : 'Selecionar template';
    }
    case 'NOTIF_INAPP': {
      const modeloId = (p.modeloNotificacaoId as string | null) ?? null;
      const para = (p.para as string) ?? '';
      return modeloId ? `Para: ${para.toLowerCase()}` : 'Selecionar template';
    }
    case 'ENVIAR_AR': {
      const modeloId = (p.modeloCartaId as string | null) ?? null;
      if (!modeloId) return 'Selecionar template';
      const cond = p.somenteSeEmailNaoAberto !== false ? 'condicional aos digitais' : 'imediato';
      return `Envio ${cond}`;
    }
    case 'CITACAO': {
      const janela = (p.janelaReversaoMinutos as number) ?? JANELA_REVERSAO_PADRAO_MINUTOS;
      return `Aciona o fluxo da Lexi · janela de ${janela} min${resumoEmailsCitacao(lerEmailsCitacao(p))}`;
    }
    case 'INTIMAR_SENTENCA': {
      const template = p.templateVersaoId ? 'template escolhido' : 'template ativo da categoria';
      return `Intima ${resumoDoAlvoDaIntimacao(p)} · ${template}`;
    }
    case 'INICIAR_PRAZO': {
      const nome = (p.nomePrazo as string) ?? '';
      const dias = (p.diasUteis as number) ?? 0;
      return nome ? `${nome} · ${dias} DU` : `${dias} DU`;
    }
    case 'AGUARDAR_EVENTO': {
      const eventos = Array.isArray(p.eventos) ? (p.eventos as string[]) : [];
      const vencimento = resumoVencimentoDaEspera(bloco);
      if (eventos.length === 0) return `Configurar evento · ${vencimento}`;
      const labels = eventos
        .map((v) => v.replace(/^arbitral\.|\.v\d+$/g, '').replaceAll('_', ' '))
        .slice(0, 2)
        .join(' / ');
      const sufixo = eventos.length > 2 ? ` +${eventos.length - 2}` : '';
      return `${labels}${sufixo} · ${vencimento}`;
    }
    case 'AGUARDAR_VISUALIZACAO':
      return `Todos os envios visualizados · ${resumoVencimentoDaEspera(bloco)}`;
    case 'AGUARDAR_CITACAO':
      return `Todos os requeridos citados · ${resumoVencimentoDaEspera(bloco)}`;
    case 'AGUARDAR_EMBARGOS': {
      const dias = (p.diasUteis as number) ?? 0;
      return `${dias} DU`;
    }
    case 'PUBLICAR_DECURSO':
      return 'Certidão de decurso · bloqueia se houver manifestação';
    case 'COBRAR_ASAAS': {
      const venc = (p.vencimentoDiasUteis as number) ?? 0;
      return `Vencimento em ${venc} DU · meio escolhido no Asaas`;
    }
    case 'REGISTRAR_CONSUMO_CONVENIO':
      return 'Registra o consumo no convênio do cliente';
    case 'IR_PARA':
      return typeof p.destinoBlocoId === 'string' && p.destinoBlocoId
        ? 'Segue para o bloco escolhido'
        : 'Escolher o bloco de destino';
    default:
      return DEFINICAO_POR_TIPO.get(tipo)?.descricao ?? '—';
  }
}

export const STATUS_PROCESSO_OPCOES: Array<{ value: ProcessoArbitralStatus; label: string }> = [
  { value: 'PROCESSO_DISTRIBUIDO', label: 'Processo Distribuído' },
  { value: 'TRIAGEM_EM_ANDAMENTO', label: 'Triagem em Andamento' },
  { value: 'TRIAGEM_CONCLUIDA', label: 'Triagem Concluída' },
  { value: 'EM_CITACAO', label: 'Em Citação' },
  { value: 'EM_ANDAMENTO', label: 'Em Andamento' },
  { value: 'SENTENCA_EMITIDA', label: 'Sentença Emitida' },
  { value: 'CONCLUIDO', label: 'Concluído' },
  { value: 'NEGADO', label: 'Negado' },
];

export function labelStatusProcesso(status: string | null): string {
  if (!status) return '—';
  const opt = STATUS_PROCESSO_OPCOES.find((o) => o.value === status);
  return opt?.label ?? status.replaceAll('_', ' ').toLowerCase();
}
